import React, { useEffect, useState } from 'react';
import { getStringfiedField, getWidgetConfig, getWidgetOptions } from '../../utils/widgets';
import CustomDropdown from '../customDropdown';
import API from '../../services/api';
import { getGranularity, getRequestPeriods, getTimeSeriesData } from '../../utils/analytics/estimatedImpressions';

const PosReport = ({ widget, query, lastUpdated, portal }) => {
  const [skus, setSkus] = useState([]);
  const [selectedSku, setSelectedSku] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSkus = async () => {
      const products = await getSkus();
      if (products && products.length > 0) {
        const options = populateOptions(products);
        const first = options[0];
        setSelectedSku(first);
      }
    };
    fetchSkus();
  }, []);

  useEffect(() => {
    if (!selectedSku || !query?.startDate || !query?.endDate) return undefined;
    let mounted = true;
    const granularity = getGranularity(query.interval);
    setIsLoading(true);
    setError(null);
    API.getAnalyticsViewData('pos_sales_uplift', {
      scope: { brandPortalId: portal.id },
      period: { ...getRequestPeriods(query, query.timezone).current, granularity },
      selection: { siteIds: query.sites || [], skuIds: [selectedSku.id] }
    }).then(response => {
      if (mounted) setData(getTimeSeriesData(response, granularity, query.timezone).data);
    }).catch(() => {
      if (mounted) setError('Failed to load sales data. Try refreshing the report.');
    }).finally(() => {
      if (mounted) setIsLoading(false);
    });
    return () => { mounted = false; };
  }, [selectedSku, JSON.stringify(query || {}), lastUpdated, portal.id]);

  async function getSkus() {
    setIsLoading(true);
    try {
      const res = await API.getProducts(portal.id);
      const products = res?.products || [];
      setSkus(products);
      return products;
    } catch (error) {
      console.error('Error fetching SKUs:', error);
      setSkus([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  }

  const populateOptions = options => {
    return options.map(option => ({
      name: option?.sku,
      id: option?.sku
    }));
  };

  return (
    <>
      <widget-container
        key={`analytics-${widget.id}`}
        id={widget.id}
        info={getStringfiedField(widget, 'info')}
        format={widget.format}
        unit={widget.unit}
        plot-options={getStringfiedField(widget, 'plotOptions')}
        widget-title={widget.title}
        data={JSON.stringify(data)}
        display="column-line"
        config={getWidgetConfig(widget.config)}
        options={getWidgetOptions(widget)}
        is-loading={isLoading || !selectedSku ? true : query?.loading}
        y-title={widget.yTitle}
        y-title-secondary={widget.yTitleSecondary}
      >
        <div slot="header-left" style={{ marginRight: 12 }}>
          <CustomDropdown
            items={populateOptions(skus)}
            valueField={'id'}
            displayField={'name'}
            outline={true}
            selectedItem={selectedSku || { id: 'loading', name: 'Loading...' }}
            handleSelection={(event, index) => {
              const options = populateOptions(skus);
              const item = options && options[index] ? options[index] : null;
              setSelectedSku(item);
            }}
          />
        </div>
      </widget-container>
      {error && <p role="alert">{error}</p>}
    </>
  );
};

export default PosReport;
